import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus,
  ChevronDown,
  ChevronUp,
  Target,
  Zap,
  Heart,
  Rocket,
  Calendar,
  Trophy,
  FileText,
  type LucideIcon,
} from 'lucide-react';
import { clsx } from 'clsx';
import type { GenerationBrief } from '@/services/api';
import { BriefLibraryCard } from './BriefLibraryCard';
import { CreativeAtomCard, type CreativeAtomData } from './CreativeAtomCard';
import { StrategyClusterHeader } from './StrategyClusterHeader';
import { CARD_SPRING, SWIM_LANE_SPRING } from './animation';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type LaneType = 'strategy' | 'campaign' | 'persona' | 'canonical';
export type ContainerKind = 'briefs' | 'creatives';

export interface SwimLaneItem {
  kind: 'brief' | 'creative';
  id: string;

  // Brief fields
  name?: string;
  status?: string;
  persona_id?: string;
  persona_name?: string;
  client_uid?: string;
  source_type?: string;
  quality_score?: number;
  created_at?: string;
  playbook_count?: number;
  ad_count?: number;

  // Creative fields
  thumbnail_url?: string;
  image_url?: string;
  media_type?: string;
  primary_text?: string;
  hook_type?: string;
  tone?: string;
  visual_style?: string;
  parent_brief_id?: string;

  // Shared
  spend?: number;
  ltv_roas?: number;
  impressions?: number;
  effectiveness_score: number;
  effectiveness_rank: number;
}

export interface SwimLaneData {
  lane_id: string;
  name: string;
  subtitle?: string;
  icon?: string;
  icon_color?: string;
  lane_type: LaneType;
  rank: number;
  score: number;
  rollup: {
    brief_count: number;
    creative_count: number;
    spend: number;
    ltv_roas: number;
    ltv_revenue: number;
    weighted_roas: number;
    causal_lift_sigma?: number | null;
    incremental_revenue?: number | null;
  };
  cluster_metadata?: {
    cluster_id: string;
    confidence: number;
    confidence_label: 'established' | 'emerging' | 'noise';
    formula_centroid: {
      hook_type?: string | null;
      tone?: string | null;
      visual_style?: string | null;
      cta_strategy?: string | null;
      framework_type?: string | null;
    };
    causal_signature: {
      drives: Array<{ concept_name: string; weight: number }>;
      resolves_concern: Array<{ concept_name: string; weight: number }>;
      inhibits: Array<{ concept_name: string; weight: number }>;
      persona_alignment: Record<string, number>;
    };
    causal_narrative?: {
      prose: string;
      annotations: Array<{
        phrase: string;
        concept_id: string;
        concept_name: string;
        concept_class: string;
        edge_type: string;
        weight: number;
        popup_title: string;
        popup_body: string;
      }>;
      generated_at?: string | null;
      model?: string | null;
    } | null;
    causal_lift_sigma?: number | null;
    is_seed: boolean;
  };
  items: SwimLaneItem[];
}

// ---------------------------------------------------------------------------
// Icon registry (Canonical lane mode)
// ---------------------------------------------------------------------------

const ICON_REGISTRY: Record<string, LucideIcon> = {
  Target,
  Zap,
  Heart,
  Rocket,
  Calendar,
  Trophy,
  FileText,
};

// ---------------------------------------------------------------------------
// Heat strip — gradient bar visualizing effectiveness across creatives in lane
// ---------------------------------------------------------------------------

function HeatStrip({ items }: { items: SwimLaneItem[] }) {
  if (items.length === 0) return null;
  // Sort scores descending so leftmost = best
  const scores = items
    .map((it) => it.effectiveness_score)
    .filter((s) => Number.isFinite(s))
    .sort((a, b) => b - a);
  if (scores.length === 0) return null;
  // Simple 3-stop gradient teal→amber→slate; transition at 60%/85% of count
  const stop1 = Math.round((1 / Math.max(scores.length, 1)) * 100);
  const stop2 = Math.round((Math.min(scores.length * 0.6, scores.length) / Math.max(scores.length, 1)) * 100);
  const stop3 = Math.round((Math.min(scores.length * 0.85, scores.length) / Math.max(scores.length, 1)) * 100);
  const gradient = `linear-gradient(to right, #14b8a6 0%, #14b8a6 ${stop1}%, #f59e0b ${stop2}%, #94a3b8 ${stop3}%, #94a3b8 100%)`;
  return (
    <div
      className="h-1 w-full rounded-full"
      style={{ background: gradient }}
      aria-label="Lane effectiveness gradient"
    />
  );
}

// ---------------------------------------------------------------------------
// Generic non-strategy headers (Canonical / Persona / Campaign)
// ---------------------------------------------------------------------------

interface GenericHeaderProps {
  lane: SwimLaneData;
  expanded: boolean;
  onToggle: () => void;
  onAdd?: () => void;
}

function GenericLaneHeader({ lane, expanded, onToggle, onAdd }: GenericHeaderProps) {
  const Icon = lane.icon ? ICON_REGISTRY[lane.icon] ?? FileText : FileText;
  const showStats = lane.rollup.brief_count > 0 || lane.rollup.creative_count > 0;
  return (
    <div
      className="flex items-center gap-3 px-4 py-3 cursor-pointer select-none"
      onClick={onToggle}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onToggle();
        }
      }}
    >
      <Icon size={16} className={lane.icon_color ?? 'text-slate-400'} />
      <span className="font-bold text-sm text-navy-900">{lane.name}</span>
      {showStats ? (
        <>
          <span className="text-xs text-slate-400">·</span>
          <span className="text-xs text-slate-500">
            {lane.rollup.brief_count} brief{lane.rollup.brief_count !== 1 ? 's' : ''}
          </span>
          {lane.rollup.creative_count > 0 && (
            <>
              <span className="text-xs text-slate-400">·</span>
              <span className="text-xs text-slate-500">
                {lane.rollup.creative_count} creative
                {lane.rollup.creative_count !== 1 ? 's' : ''}
              </span>
            </>
          )}
          {lane.rollup.spend > 0 && (
            <>
              <span className="text-xs text-slate-400">·</span>
              <span className="text-xs text-slate-500">
                ${(lane.rollup.spend / 1000).toFixed(1)}k spend
              </span>
            </>
          )}
        </>
      ) : lane.subtitle ? (
        <span className="text-xs text-slate-400 font-normal truncate">· {lane.subtitle}</span>
      ) : null}
      <div className="ml-auto flex items-center gap-2">
        {onAdd && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onAdd();
            }}
            className="w-6 h-6 rounded-md flex items-center justify-center text-slate-400 hover:text-teal-600 hover:bg-teal-50 transition"
            title={`Add brief to ${lane.name}`}
          >
            <Plus size={14} />
          </button>
        )}
        {expanded ? (
          <ChevronUp size={14} className="text-slate-400" />
        ) : (
          <ChevronDown size={14} className="text-slate-400" />
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SwimLane — single lane, polymorphic header + animated card grid.
// ---------------------------------------------------------------------------

export interface SwimLaneProps {
  lane: SwimLaneData;
  container: ContainerKind;
  onOpenBrief: (briefId: string) => void;
  onOpenCreative?: (creativeId: string) => void;
  onAddBrief?: (lane: SwimLaneData) => void;
  onRequestRecompute?: () => void;
  defaultExpanded?: boolean;
}

export function SwimLane({
  lane,
  container,
  onOpenBrief,
  onOpenCreative,
  onAddBrief,
  onRequestRecompute,
  defaultExpanded = true,
}: SwimLaneProps) {
  const storageKey = `swim-lane-expanded:${lane.lane_type}:${lane.lane_id}`;
  const [expanded, setExpanded] = useState<boolean>(() => {
    if (typeof window === 'undefined') return defaultExpanded;
    try {
      const raw = window.sessionStorage.getItem(storageKey);
      if (raw === 'true') return true;
      if (raw === 'false') return false;
    } catch {
      // ignore
    }
    return defaultExpanded;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.sessionStorage.setItem(storageKey, String(expanded));
    } catch {
      // quota / private mode — silent
    }
  }, [expanded, storageKey]);

  const isStrategy = lane.lane_type === 'strategy';
  const isNoise =
    isStrategy &&
    (lane.cluster_metadata?.confidence_label === 'noise' ||
      lane.lane_id === 'noise');

  const itemsToRender = lane.items;

  return (
    <motion.section
      layout
      transition={SWIM_LANE_SPRING}
      className={clsx(
        'bg-white border rounded-xl overflow-hidden',
        isStrategy && lane.cluster_metadata?.confidence_label === 'emerging'
          ? 'border-amber-200 border-dashed'
          : isNoise
            ? 'border-slate-200 border-dashed'
            : 'border-slate-200',
      )}
    >
      {isStrategy ? (
        <StrategyClusterHeader
          lane={lane}
          expanded={expanded}
          onToggle={() => setExpanded((v) => !v)}
          onAdd={onAddBrief ? () => onAddBrief(lane) : undefined}
          onRequestRecompute={onRequestRecompute}
        />
      ) : (
        <GenericLaneHeader
          lane={lane}
          expanded={expanded}
          onToggle={() => setExpanded((v) => !v)}
          onAdd={onAddBrief ? () => onAddBrief(lane) : undefined}
        />
      )}

      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            key="lane-body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22, ease: 'easeInOut' }}
            className="border-t border-slate-100"
          >
            {itemsToRender.length === 0 ? (
              <div className="px-4 py-6 flex flex-col items-center gap-2 text-center">
                <div className="text-xs text-slate-400">
                  {container === 'creatives'
                    ? 'No creatives in this lane yet.'
                    : 'No briefs in this lane yet.'}
                </div>
                {onAddBrief && container === 'briefs' && (
                  <button
                    type="button"
                    onClick={() => onAddBrief(lane)}
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-teal-600 hover:text-teal-700"
                  >
                    <Plus size={12} />
                    Create your first {lane.name} brief →
                  </button>
                )}
              </div>
            ) : (
              <div className="px-4 pt-3 pb-4 space-y-3">
                {container === 'creatives' && <HeatStrip items={itemsToRender} />}
                <motion.div
                  layout
                  transition={SWIM_LANE_SPRING}
                  className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3"
                >
                  <AnimatePresence mode="popLayout" initial={false}>
                    {itemsToRender.map((item) => (
                      <motion.div
                        key={`${container}:${item.id}`}
                        layout
                        layoutId={`${container}:${item.id}`}
                        initial={{ scale: 0.95, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.9, opacity: 0 }}
                        transition={CARD_SPRING}
                      >
                        {container === 'creatives' ? (
                          <CreativeAtomCard
                            creative={item as CreativeAtomData}
                            onClick={() =>
                              onOpenCreative
                                ? onOpenCreative(item.id)
                                : item.parent_brief_id && onOpenBrief(item.parent_brief_id)
                            }
                            effectivenessPercentile={item.effectiveness_score * 100}
                          />
                        ) : (
                          (() => {
                            const { brief, kpis } = briefItemToGenerationBrief(item);
                            return (
                              <BriefLibraryCard
                                brief={brief}
                                kpis={kpis}
                                onClick={() => onOpenBrief(item.id)}
                              />
                            );
                          })()
                        )}
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </motion.div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.section>
  );
}

// ---------------------------------------------------------------------------
// SwimLaneItem (brief) → minimal GenerationBrief shape for BriefLibraryCard
// ---------------------------------------------------------------------------

export interface BriefLaneKpis {
  rank: number;
  ad_count: number;
  spend: number;
  ltv_roas: number;
  impressions: number;
  effectiveness_score: number;
}

function briefItemToGenerationBrief(item: SwimLaneItem): {
  brief: GenerationBrief;
  kpis: BriefLaneKpis;
} {
  const brief = {
    brief_id: item.id,
    name: item.name ?? item.id,
    client_uid: item.client_uid ?? '',
    persona_id: item.persona_id ?? '',
    audience: item.persona_name ? { persona_name: item.persona_name } : undefined,
    status: item.status ?? 'draft',
    version: 1,
    created_at: item.created_at,
    playbook_ids: Array.from({ length: item.playbook_count ?? 0 }, (_, i) => `pb_${i}`),
    provenance: item.source_type ? { source_type: item.source_type } : undefined,
    quality:
      item.quality_score !== undefined
        ? { confidence_score: item.quality_score }
        : undefined,
  } as unknown as GenerationBrief;

  const kpis: BriefLaneKpis = {
    rank: item.effectiveness_rank,
    ad_count: item.ad_count ?? 0,
    spend: item.spend ?? 0,
    ltv_roas: item.ltv_roas ?? 0,
    impressions: item.impressions ?? 0,
    effectiveness_score: item.effectiveness_score,
  };

  return { brief, kpis };
}
